import share from '../../backgroundImages/share.webp';

/**
 * FAQ Data organized into groups.
 */
export const faqData = [
  // GROUP 1: GETTING STARTED / USAGE
  {
    id: 'post-lost-found',
    group: 'usage',
    groupLabel: 'Getting Started',
    question: 'How do I post a lost or found animal?',
    answer: "Navigate to the 'Post Lost or Found Animal' button on the home screen. Fill out the form with details about the animal including breed, size, date, and upload a photo. Your post will be visible to the community immediately.",
    keywords: 'post lost found animal report missing pet create submit form',
    useTranslation: false,
  },
  {
    id: 'contact-post',
    group: 'usage',
    question: 'How do I contact someone about their post?',
    answer: "Click on any post card to view details, then click the 'Contact Owner' or 'Contact Sitter' button. This will take you to a message composition screen where you can write your first message.",
    keywords: 'contact owner sitter message someone about post reach out chat',
    useTranslation: false,
  },
  {
    id: 'use-without-account',
    group: 'usage',
    question: 'Can I use the app without creating an account?',
    answer: "Yes! You can browse all posts, view emergency resources, and search for local animal services without an account. However, you'll need to sign in to favorite posts, create your own posts, or message other users.",
    keywords: 'without account guest sign in browse anonymous no login register',
    useTranslation: false,
  },
  {
    id: 'change-location',
    group: 'usage',
    question: 'How do I change my location?',
    answer: 'Click on your current location at the top of the home screen or go to Menu > Account Settings > Location. You can search for any city or zip code to update your location.',
    keywords: 'change location city zip code update area set postal',
    useTranslation: false,
  },
  {
    id: 'injured-animal',
    group: 'usage',
    question: 'What should I do if I find an injured animal?',
    answer: "Click the yellow emergency button in the top right corner. Search for the type of emergency (e.g., 'injured bird', 'sick seagull') and you'll get immediate access to phone numbers and resources for help in your area.",
    keywords: 'injured hurt sick animal emergency wounded bird help rescue',
    useTranslation: false,
  },

  // GROUP 2: CONTACT & SUPPORT
  {
    id: 'tech-issues',
    group: 'contact',
    groupLabel: 'Contact & Support',
    questionKey: 'faq_category_tech_issues',
    answerKey: 'faq_tech_issues_text',
    email: 'support@allanimalapp.com',
    keywords: 'technical issue problem bug support not working error trouble crash',
    useTranslation: true,
  },
  {
    id: 'advertising',
    group: 'contact',
    questionKey: 'faq_category_advertising',
    answerKey: 'faq_advertising_text',
    email: 'marketing@allanimalapp.com',
    keywords: 'advertise advertising marketing promote business ad partnership sponsor',
    useTranslation: true,
  },
  {
    id: 'general-inquiries',
    group: 'contact',
    questionKey: 'faq_category_general_inquiries',
    answerKey: 'faq_general_inquiries_text',
    email: 'info@allanimalapp.com',
    keywords: 'general inquiry question contact info feedback email reach',
    useTranslation: true,
  },
  {
    id: 'report-content',
    group: 'contact',
    questionKey: 'report_inappropriate_content',
    answerKey: 'report_inappropriate_content_text',
    email: 'support@allanimalapp.com',
    keywords: 'report inappropriate content abuse spam offensive flag block',
    useTranslation: true,
  },

  // GROUP 3: APP INFO
  {
    id: 'browser-compatibility',
    group: 'info',
    groupLabel: 'App Information',
    questionKey: 'faq_category_browser_compatibility',
    answerKey: 'faq_text_browser_compatibility',
    keywords: 'browser compatibility chrome safari firefox edge supported device',
    useTranslation: true,
  },
  {
    id: 'language-availability',
    group: 'info',
    questionKey: 'faq_category_language_availability',
    answerKey: 'faq_text_language_availability',
    keywords: 'language translation available languages translate localization',
    useTranslation: true,
  },

  // GROUP 4: INSTALLATION / ADD TO HOME SCREEN
  {
    id: 'add-home-iphone',
    group: 'installation',
    groupLabel: 'Add to Home Screen',
    question: 'How do I add to Home Screen on iPhone (Safari)?',
    answer: `To add the All Animal App to your Home Screen on an iPhone using Safari:
1. Open Safari and go to allanimalapp.com.
2. Tap the Share icon (square with arrow pointing up).
3. Scroll down and tap "Add to Home Screen."
4. Edit the name if you want.
5. Tap "Add" in the top right corner.
6. Done! The web app icon appears on your Home Screen like an app.

⚠ This only works in Safari. Chrome or Firefox on iOS won't show the "Add to Home Screen" option due to iOS limitations.`,
    keywords: 'add home screen iphone ios safari install app icon pwa shortcut',
    image: share,
    useTranslation: false,
  },
  {
    id: 'add-home-android-chrome',
    group: 'installation',
    question: 'How do I add to Home Screen on Android (Chrome)?',
    answer: `To add the All Animal App to your Home Screen on an Android device using Chrome:
1. Open Chrome and go to allanimalapp.com.
2. Tap the three-dot menu in the top-right corner.
3. Tap "Add to Home screen."
4. Edit the name if desired, then tap "Add."
5. You might be asked to drag the icon or confirm with "Add automatically."
6. The icon now appears on your Home Screen.`,
    keywords: 'add home screen android chrome install app icon pwa shortcut',
    useTranslation: false,
  },
  {
    id: 'add-home-android-firefox',
    group: 'installation',
    question: 'How do I add to Home Screen on Android (Firefox)?',
    answer: `To add the All Animal App to your Home Screen on an Android device using Firefox:
1. Open the page in Firefox.
2. Tap the three-dot menu.
3. Tap "Add to Home screen."`,
    keywords: 'add home screen android firefox install app icon pwa shortcut',
    useTranslation: false,
  },
  {
    id: 'add-home-samsung',
    group: 'installation',
    question: 'How do I add to Home Screen on Samsung Internet?',
    answer: `To add the All Animal App to your Home Screen on an Android device using Samsung Internet:
1. Visit the site.
2. Tap the menu (three lines or dots).
3. Tap "Add page to" > "Home screen."`,
    keywords: 'add home screen samsung internet install app icon pwa shortcut',
    useTranslation: false,
  },
];

export const getGroups = () => {
  const groups = [];
  const seen = new Set();

  faqData.forEach((faq) => {
    if (!seen.has(faq.group)) {
      seen.add(faq.group);
      groups.push({
        key: faq.group,
        label: faq.groupLabel || faq.group,
      });
    }
  });

  return groups;
};

/**
 * @param {Function} t
 * @returns {Array}
 */
export const buildFaqSearchEntries = (t) =>
  faqData.map((faq) => {
    const question = faq.useTranslation ? t(faq.questionKey) : faq.question;
    const answer = faq.useTranslation ? t(faq.answerKey) : faq.answer;
    const preview = (answer || '').replace(/\s+/g, ' ').trim().slice(0, 120);

    return {
      id: faq.id,
      faqId: faq.id,
      type: 'faq',
      title: question,
      description: preview,
      queryKeyword: faq.keywords || '',
      key: `faq-${faq.id}`,
      group: faq.group,
    };
  });
